import type { Ref } from 'vue';
import type { TZDate } from '@date-fns/tz';

import { computed } from 'vue';
import { useRoute } from 'vue-router';

import * as routeQueryHelpers from '@/helpers/routeQuery';
import { dispatchJobsTabs } from '@/constants/tabs';

export interface DefRouteQuery {
  tab: keyof typeof dispatchJobsTabs;
  date?: TZDate;
  jobSearch?: string;
  jobServiceAreas?: string[];
  jobServiceGroups?: string[];
  jobSites?: string[];
  jobGoods?: string[];
  isJobCanSchedule?: boolean;
  isJobPayOnHold?: boolean;
  isJobInvoice?: boolean;
  driverSearch?: string;
  driverFleets?: string[];
  driverType?: string[];
  driverZone?: string[];
}
export interface RouteQueryComposable {
  tab: Readonly<Ref<DefRouteQuery['tab']>>;
  date: Readonly<Ref<DefRouteQuery['date']>>;
  jobSearch: Readonly<Ref<DefRouteQuery['jobSearch']>>;
  jobServiceAreas: Readonly<Ref<DefRouteQuery['jobServiceAreas']>>;
  jobServiceGroups: Readonly<Ref<DefRouteQuery['jobServiceGroups']>>;
  jobSites: Readonly<Ref<DefRouteQuery['jobSites']>>;
  jobGoods: Readonly<Ref<DefRouteQuery['jobGoods']>>;
  isJobCanSchedule: Readonly<Ref<DefRouteQuery['isJobCanSchedule']>>;
  isJobPayOnHold: Readonly<Ref<DefRouteQuery['isJobPayOnHold']>>;
  isJobInvoice: Readonly<Ref<DefRouteQuery['isJobInvoice']>>;
  driverSearch: Readonly<Ref<DefRouteQuery['driverSearch']>>;
  driverFleets: Readonly<Ref<DefRouteQuery['driverFleets']>>;
  driverType: Readonly<Ref<DefRouteQuery['driverType']>>;
  driverZone: Readonly<Ref<DefRouteQuery['driverZone']>>;
}

export default function useDispatchRouteQuery(
  defRouteQuery: DefRouteQuery,
): RouteQueryComposable {
  const _defTab = defRouteQuery.tab;
  const _defDate = defRouteQuery.date;
  const _defJobSearch = defRouteQuery.jobSearch;
  const _defJobServiceAreas = defRouteQuery.jobServiceAreas;
  const _defJobServiceGroups = defRouteQuery.jobServiceGroups;
  const _defJobSites = defRouteQuery.jobSites;
  const _defJobGoods = defRouteQuery.jobGoods;
  const _defIsJobCanSchedule = defRouteQuery.isJobCanSchedule;
  const _defIsJobPayOnHold = defRouteQuery.isJobPayOnHold;
  const _defIsJobInvoice = defRouteQuery.isJobInvoice;
  const _defDriverSearch = defRouteQuery.driverSearch;
  const _defDriverFleets = defRouteQuery.driverFleets;
  const _defDriverType = defRouteQuery.driverType;
  const _defDriverZone = defRouteQuery.driverZone;

  const route = useRoute();

  const tab = computed<DefRouteQuery['tab']>(() => {
    if (typeof route.query.tab !== 'string') return _defTab;
    if (Object.keys(dispatchJobsTabs).includes(route.query.tab)) {
      return route.query.tab as DefRouteQuery['tab'];
    }
    return _defTab;
  });

  const date = computed<DefRouteQuery['date']>(() => {
    return routeQueryHelpers.getApiZonedQueryDateValue(
      route.query.date,
      _defDate,
    );
  });

  const jobSearch = computed<DefRouteQuery['jobSearch']>(() => {
    return routeQueryHelpers.getQueryStringValue(
      route.query.job_search,
      _defJobSearch,
    );
  });

  const jobServiceAreas = computed<DefRouteQuery['jobServiceAreas']>(() => {
    return routeQueryHelpers.getQueryStringListValue(
      route.query.job_service_areas,
      _defJobServiceAreas,
    );
  });

  const jobServiceGroups = computed<DefRouteQuery['jobServiceGroups']>(() => {
    return routeQueryHelpers.getQueryStringListValue(
      route.query.job_service_groups,
      _defJobServiceGroups,
    );
  });

  const jobSites = computed<DefRouteQuery['jobSites']>(() => {
    return routeQueryHelpers.getQueryStringListValue(
      route.query.job_sites,
      _defJobSites,
    );
  });

  const jobGoods = computed<DefRouteQuery['jobGoods']>(() => {
    return routeQueryHelpers.getQueryStringListValue(
      route.query.job_goods,
      _defJobGoods,
    );
  });

  const isJobCanSchedule = computed<DefRouteQuery['isJobCanSchedule']>(() => {
    return routeQueryHelpers.getQueryBooleanValue(
      route.query.is_job_can_schedule,
      _defIsJobCanSchedule,
    );
  });

  const isJobPayOnHold = computed<DefRouteQuery['isJobPayOnHold']>(() => {
    return routeQueryHelpers.getQueryBooleanValue(
      route.query.is_job_pay_on_hold,
      _defIsJobPayOnHold,
    );
  });

  const isJobInvoice = computed<DefRouteQuery['isJobInvoice']>(() => {
    return routeQueryHelpers.getQueryBooleanValue(
      route.query.is_job_invoice,
      _defIsJobInvoice,
    );
  });

  const driverSearch = computed<DefRouteQuery['driverSearch']>(() => {
    return routeQueryHelpers.getQueryStringValue(
      route.query.driver_search,
      _defDriverSearch,
    );
  });

  const driverFleets = computed<DefRouteQuery['driverFleets']>(() => {
    return routeQueryHelpers.getQueryStringListValue(
      route.query.driver_fleets,
      _defDriverFleets,
    );
  });

  const driverType = computed<DefRouteQuery['driverType']>(() => {
    return routeQueryHelpers.getQueryStringListValue(
      route.query.driver_type,
      _defDriverType,
    );
  });

  const driverZone = computed<DefRouteQuery['driverZone']>(() => {
    return routeQueryHelpers.getQueryStringListValue(
      route.query.driver_zone,
      _defDriverZone,
    );
  });

  return {
    tab,
    date,
    jobSearch,
    jobServiceAreas,
    jobServiceGroups,
    jobSites,
    jobGoods,
    isJobCanSchedule,
    isJobPayOnHold,
    isJobInvoice,
    driverSearch,
    driverFleets,
    driverType,
    driverZone,
  };
}
